import React, { ReactNode, ReactElement } from 'react';
import { cn } from '@/lib/utils';

export type ColorVariant = 'purple' | 'green' | 'amber' | 'blue' | 'red' | 'indigo' | 'gray' | 'orange' | 'brown' | 'white' | 'black';

interface BaseButtonProps {
  /**
   * The color variant of the button
   * @default 'purple'
   */
  variant?: ColorVariant;
  
  /**
   * Whether the button is in an active/selected state
   * @default false
   */
  isActive?: boolean;
  
  /**
   * Optional count to display in a badge
   */
  count?: number;
  
  /**
   * Custom element to display instead of count (for loading states)
   */
  countElement?: ReactNode;
  
  /**
   * Additional class names to apply to the button
   */
  className?: string;
  
  /**
   * Optional click handler
   */
  onClick?: () => void;
  
  /**
   * Button content (can be text, icon, or any React node)
   */
  children: ReactNode;
}

const variantClasses = {
  // Base styles for all variants
  base: [
    'group w-full justify-start text-left font-normal',
    'border bg-background text-foreground h-9 rounded-md px-3',
    // Enhanced transitions for smoother hover and click effects
    'transform transition-all duration-300 ease-in-out',
    'hover:scale-[1.02] hover:shadow-lg',
    'active:scale-[0.98] active:shadow-md',
    'disabled:transform-none disabled:shadow-none',
    'inline-flex items-center gap-3 whitespace-nowrap text-sm',
    'ring-offset-background focus-visible:outline-none',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    // Apply transitions to all properties for smoother effects
    'transition-all', // Apply transition to all properties
  ],
  
  // Icon container styles
  iconContainer: [
    'p-1 rounded-md mr-2',
    'bg-opacity-70 dark:bg-opacity-40',
  ],
  // Color variants
  variants: {
    purple: {
      base: 'border-purple-200/50 dark:border-purple-900/30',
      hover: 'hover:border-purple-300/70 dark:hover:border-purple-700/50 hover:bg-purple-50/80 dark:hover:bg-purple-900/30',
      active: 'ring-2 ring-offset-2 ring-purple-500/20',
      count: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
    },
    green: {
      base: 'border-green-200/50 dark:border-green-900/30',
      hover: 'hover:border-green-300/70 dark:hover:border-green-700/50 hover:bg-green-50/80 dark:hover:bg-green-900/30',
      active: 'ring-2 ring-offset-2 ring-green-500/20',
      count: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    },
    amber: {
      base: 'border-amber-200/50 dark:border-amber-900/30',
      hover: 'hover:border-amber-300/70 dark:hover:border-amber-700/50 hover:bg-amber-50/80 dark:hover:bg-amber-900/30',
      active: 'ring-2 ring-offset-2 ring-amber-500/20',
      count: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    },
    blue: {
      base: 'border-blue-200/50 dark:border-blue-900/30',
      hover: 'hover:border-blue-300/70 dark:hover:border-blue-700/50 hover:bg-blue-50/80 dark:hover:bg-blue-900/30',
      active: 'ring-2 ring-offset-2 ring-blue-500/20',
      count: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    },
    red: {
      base: 'border-red-200/50 dark:border-red-900/30',
      hover: 'hover:border-red-300/70 dark:hover:border-red-700/50 hover:bg-red-50/80 dark:hover:bg-red-900/30',
      active: 'ring-2 ring-offset-2 ring-red-500/20',
      count: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    },
    indigo: {
      base: 'border-indigo-200/50 dark:border-indigo-900/30',
      hover: 'hover:border-indigo-300/70 dark:hover:border-indigo-700/50 hover:bg-indigo-50/80 dark:hover:bg-indigo-900/30',
      active: 'ring-2 ring-offset-2 ring-indigo-500/20',
      count: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300',
    },
    gray: {
      base: 'border-gray-200/50 dark:border-gray-900/30',
      hover: 'hover:border-gray-300/70 dark:hover:border-gray-700/50 hover:bg-gray-50/80 dark:hover:bg-gray-900/30',
      active: 'ring-2 ring-offset-2 ring-gray-500/20',
      count: 'bg-gray-100 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300',
    },
    orange: {
      base: 'border-orange-200/50 dark:border-orange-900/30',
      hover: 'hover:border-orange-300/70 dark:hover:border-orange-700/50 hover:bg-orange-50/80 dark:hover:bg-orange-900/30',
      active: 'ring-2 ring-offset-2 ring-orange-500/20',
      count: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
    }
  } as const,
};

/**
 * A flexible sidebar filter button with color variants
 */
export const SidebarFilterButton = React.forwardRef<
  HTMLButtonElement,
  BaseButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({
  variant = 'purple',
  isActive = false,
  count,
  countElement,
  className,
  onClick,
  children,
  ...props
}, ref) => {
  // Make sure variantStyle is always defined by defaulting to purple if the variant doesn't exist
  const variantStyle = variantClasses.variants[variant] || variantClasses.variants.purple;
  
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={cn(
        variantClasses.base,
        variantStyle.base,
        variantStyle.hover,
        isActive ? variantStyle.active : '',
        className
      )}
      {...props}
    >
      {React.Children.map(children, (child, index) => {
        // Handle text nodes first
        if (typeof child === 'string' || typeof child === 'number') {
          return <span className="flex-grow">{child}</span>;
        }
        
        // Handle React elements
        if (React.isValidElement(child)) {
          const element = child as ReactElement;
          
          // Handle Lucide icons (they have className prop)
          if (typeof element.type === 'function' && 
              element.props && 
              'className' in element.props) {
            const iconColor = `text-${variant}-500`;
            return React.cloneElement(element, {
              size: 14, // Explicitly set size to 14px for consistency
              className: cn('mr-2', iconColor, element.props.className)
            });
          }
          
          // Handle SVG elements
          if (element.type === 'svg') {
            const iconColor = `text-${variant}-500`;
            return React.cloneElement(element, {
              className: cn('h-4 w-4 mr-2', iconColor, element.props.className)
            });
          }
          
          // Handle span elements (add flex-grow)
          if (element.type === 'span' && 
              (!element.props.className || !element.props.className.includes('ml-auto'))) {
            return React.cloneElement(element, {
              className: cn('flex-grow', element.props.className)
            });
          }
        }
        
        // Return unchanged if no conditions match
        return child;
      })}
      {countElement ? (
        countElement
      ) : count !== undefined && (
        <span 
          className={cn(
            'ml-auto px-2 py-0.5 text-xs font-medium rounded-full',
            variantStyle.count
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
});

SidebarFilterButton.displayName = 'SidebarFilterButton';

/**
 * A container component for a group of SidebarFilterButtons
 */
interface SidebarFilterButtonsProps {
  /**
   * Array of button configurations
   */
  buttons: Array<{
    /** Button content (text, icon, etc.) */
    children: ReactNode;
    /** Optional count badge */
    count?: number;
    /** Custom element to display instead of count (for loading states) */
    countElement?: ReactNode;
    /** Whether the button is active */
    isActive?: boolean;
    /** Color variant */
    variant?: ColorVariant;
    /** Click handler */
    onClick?: () => void;
    /** Additional class names */
    className?: string;
  }>;
  
  /**
   * Additional class names for the container
   */
  className?: string;
  
  /**
   * Spacing between buttons
   * @default 'space-y-2'
   */
  spacing?: string;
}

/**
 * A container for a group of SidebarFilterButtons
 */
export const SidebarFilterButtons: React.FC<SidebarFilterButtonsProps> = ({
  buttons,
  className,
  spacing = 'space-y-2',
}) => {
  return (
    <div className={cn(spacing, className)}>
      {buttons.map((button, index) => (
        <SidebarFilterButton
          key={`${button.variant || 'purple'}-${typeof button.children === 'string' ? button.children : index}`}
          variant={button.variant}
          isActive={button.isActive}
          count={button.count}
          countElement={button.countElement}
          onClick={button.onClick}
          className={button.className}
        >
          {button.children}
        </SidebarFilterButton>
      ))}
    </div>
  );
};

export default SidebarFilterButtons;
